/**
 * workOrderCacheSync — keeps work-order-derived query caches honest even when
 * nothing that displays them is on screen.
 *
 * The announcements in utils/workOrderEvents are delivered to whoever is
 * listening at the time. Until now the only listener for the location
 * dashboard's counts lived INSIDE the location dashboard, so the counts only
 * refreshed while the dashboard was already mounted. That is exactly the case
 * that never happens for a close: closing a work order happens on the
 * work-orders view, and App renders one view at a time, so the dashboard is
 * unmounted and hears nothing. Coming back mounted the query again — but the
 * cached entry was still inside the app-wide 30s staleTime, so React Query
 * served it as-is and the closed count sat at its old value until the user
 * reloaded the page.
 *
 * Subscribing here instead fixes that class of bug rather than that one bug:
 * the queryClient is a module singleton and outlives every view, so an
 * announcement always lands. Marking an INACTIVE query stale costs no
 * network — it just guarantees the next mount refetches instead of serving a
 * cache the mutation already invalidated. Active queries refetch immediately,
 * as they did before.
 *
 * Views keep their own subscriptions only where they need something a cache
 * invalidation cannot express (scrolling, highlighting, non-React-Query
 * state).
 */
import type { QueryClient } from '@tanstack/react-query';
import { onWorkOrderChanged, onWorkOrderClosed, onWorkOrderCreated } from '../utils/workOrderEvents';
import { queryKeys } from './queryKeys';

/**
 * Every cache a work-order mutation can move for one location. `locationId`
 * is best-effort on the change announcement — a URL does not always carry
 * one — so an announcement without it invalidates the dashboards and queues
 * for ALL locations. A wasted invalidation is nearly free (inactive queries
 * are only marked stale); a missed one is the bug.
 */
export function invalidateForLocation(queryClient: QueryClient, locationId?: string): void {
  if (locationId) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.locations.dashboardAll(locationId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoicing.queueAll(locationId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.approvals.queueAll(locationId) });
    return;
  }
  void queryClient.invalidateQueries({ queryKey: ['locations', 'dashboard'] });
  void queryClient.invalidateQueries({ queryKey: ['invoicing', 'queue'] });
  void queryClient.invalidateQueries({ queryKey: ['approvals', 'queue'] });
}

/**
 * Wire the announcements to the cache. Call once at startup; returns an
 * unsubscribe for tests.
 */
export function registerWorkOrderCacheSync(queryClient: QueryClient): () => void {
  const unsubscribers = [
    onWorkOrderCreated((detail) => invalidateForLocation(queryClient, detail.locationId)),
    onWorkOrderClosed((detail) => invalidateForLocation(queryClient, detail.locationId)),
    onWorkOrderChanged((detail) => invalidateForLocation(queryClient, detail.locationId)),
  ];
  return () => unsubscribers.forEach((off) => off());
}
